import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { getSelectionList } from 'src/components/ImportResults/utils';
import { notification } from 'src/components/Notification';
import { useGetPastJobListOfFactory, useImportJobResults } from 'src/hooks/job';
import Button from 'src/components/Button';
import SelectBox from 'src/components/SelectBox';
import Checkbox from 'src/components/Checkbox';
import Style from 'src/components/ImportResults/ImportResults.styles';
import logger from 'src/utils/logger';
import Loader from 'src/components/Loader';

const ImportResults = (props) => {

  const {
    onCancel,
    jobId,
    factoryCompanies,
    companyTypes,
    reloadJobResults,
  } = props;
  const { t } = useTranslation();
  const [confirmationInput, setConfirmationInput] = useState('');
  const [importInfo, setImportInfo] = useState({
    jobFromId: null,
    jobToId: jobId,
    importChecklists: false,
    importDatasheets: false,
  });
  const [expanded, setExpanded] = useState(false);
  const [allSelectionListItems, setAllSelectionListItems] = useState([]);
  const [processedPastJobsCounter, setProcessedPastJobsCounter] = useState(0);
  const [processedAllPastJobs, setProcessedAllPastJobs] = useState(false);
  const [importing, setImporting] = useState(false);

  const canImport = importInfo.jobFromId &&
    confirmationInput === t('IMPORT_CONFIRM') &&
    Object.values(importInfo).includes(true);

  const menuOpenCloseHandlers = useMemo(() => ({
    onMenuOpen: () => {
      setExpanded(true);
    },
    onMenuClose: () => {
      setExpanded(false);
    },
  }), [true]);

  const importResults = useImportJobResults(jobId);

  const [getPastJobListOfFactory, { loading: pastFactoryJobLoading, data: pastJobs }] = useGetPastJobListOfFactory({});

  const onImport = async () => {
    setImporting(true);
    try {
      const {
        data: {
          importJobResults: {
            success,
            checklistQuestionsWithErrors,
          },
        },
      } = await importResults({ ...importInfo });
      if (!success) {
        notification.error(t('IMPORT_RESULTS_FAILED'));
      }else if (checklistQuestionsWithErrors && checklistQuestionsWithErrors.length) {
        await reloadJobResults();
        notification.warn(t('IMPORT_RESULTS_FAILED_FOR_SOME'));
      }else {
        await reloadJobResults();
        notification.success(t('IMPORT_RESULTS_SUCCESS'));
      }
    } catch (err) {
      logger.error(err);
      notification.error(t('IMPORT_RESULTS_FAILED'));
    } finally {
      setImporting(false);
      /*
      cancel currently don't do anything special other than closing popup window
       */
      onCancel();
    }
  };

  const fetchPastJobListOfFactory = async () => {
    (factoryCompanies || []).map(async ({ company }, index) => {
      getPastJobListOfFactory({
        variables: {
          auditStartTo: new Date(),
          factoryId: company.id,
        },
      });
    });
  };

  useEffect(() => {
    fetchPastJobListOfFactory();
  }, []);

  useEffect(() => {
    const exec = async () => {
      const selectionList = await getSelectionList(pastJobs?.jobs?.filter(job => jobId !== job.id) || [], companyTypes);
      setAllSelectionListItems(allSelectionListItems.concat(selectionList));
      setProcessedPastJobsCounter(prevState => prevState + 1);
    };
    if (pastJobs) {
      exec();
    }
  }, [pastJobs]);

  useEffect(() => {
    if (processedPastJobsCounter === factoryCompanies.length) {
      setProcessedAllPastJobs(true);
    }
  }, [processedPastJobsCounter]);

  return (
    <Style.ImportResultPopup expanded={expanded} isCloseHidden={true}>
      <p>{t('IMPORT_RESULTS_SELECTION_TITLE')}:</p>
      <br />
      <SelectBox
        maxMenuHeight={'15rem'}
        minMenuHeight={'15rem'}
        {...menuOpenCloseHandlers}
        isDisabled={!processedAllPastJobs || importing}
        isLoading={!processedAllPastJobs}
        onChange={(selectedOption) => {
          setImportInfo({ ...importInfo, jobFromId: selectedOption.value });
        }}
        options={allSelectionListItems.map(o => ({ value: o.id, label: o.displayText }))}
        required={true}
      />
      <Style.LowerSection expanded={expanded}>
        <p>{t('IMPORT_RESULTS_OPTION_TITLE')}:</p>
        <br />
        <Style.CheckboxContainer>
          <Checkbox className={`${Style.Classes['option-checkbox']}`}
                    disabled={importing}
                    label={<Trans
                      components={{ bold: <strong /> }}
                      i18nKey="IMPORT_CHECKLIST_RESULTS_OPTION_INFO"
                    />}
                    onChange={(e) => {
                      setImportInfo({ ...importInfo, importChecklists: e.target.checked });
                    }}
                    reverse={true}
                    />
          <br />
          <Checkbox className={Style.Classes['option-checkbox']}
                    disabled={importing}
                    label={<Trans
                      components={{ bold: <strong /> }}
                      i18nKey="IMPORT_DATASHEET_RESULTS_OPTION_INFO" />}
                    onChange={(e) => {
                      setImportInfo({ ...importInfo, importDatasheets: e.target.checked });
                    }}
                    reverse={true}
                    />
        </Style.CheckboxContainer>
        <p className={Style.Classes['warning-text']}>
          <span>
            {t('IMPORT_RESULTS_WARNING_TITLE')}
          </span>:&nbsp;
          {t('IMPORT_RESULTS_WARNING_TEXT')}
        </p>
        <p><Trans
          components={{ italic: <em /> }}
          i18nKey="IMPORT_RESULTS_CONFIRMATION_PROMPT_TITLE"
        /></p>
        <Style.ConfirmTextField
          disabled={importing}
          onChange={(e) => setConfirmationInput(e.target.value)}
          placeholder={t('IMPORT_CONFIRM')}
          required={true}
          value={confirmationInput} />
        {!importing &&
          <div className={Style.Classes['buttons-container']}>
            <Button disabled={!canImport} gray={!canImport} onClick={onImport} sm>{t('IMPORT')}</Button>
            <Button ghost onClick={onCancel} orange sm>{t('CANCEL')}</Button>
          </div>
        }
        {importing && <Loader withText={false}/>}
      </Style.LowerSection>
    </Style.ImportResultPopup>
  );
};

ImportResults.propTypes = {
  onCancel: PropTypes.func,
  jobId: PropTypes.number,
  factoryCompanies: PropTypes.array,
  companyTypes: PropTypes.array,
  reloadJobResults: PropTypes.func,
};

export default React.memo(ImportResults);
